import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { collection, doc, getDoc, getDocs, orderBy, writeBatch } from "firebase/firestore";
import { db } from "../lib/firebase";
import { useAuth } from "../hooks/useAuth";
import { useCouple } from "../hooks/useCouple";
import { useCoupleCollection } from "../hooks/useCoupleCollection";
import { markSeen } from "../hooks/useUnseenBadges";
import { Fab } from "../components/Fab";
import { Sheet } from "../components/Sheet";
import type { ListItem, WirList } from "../types";

// Legacy shopping-list items (from before multi-list support) are migrated
// once into a regular list under this fixed id, so nothing the couple
// already entered gets lost in the switch. The original shoppingItems/
// documents are left in place untouched as a safety copy — the new UI
// simply never reads them again once this has run.
const LEGACY_LIST_ID = "shopping-default";

const ICON_CHOICES = ["🛒", "📦", "🎒", "✈️", "🎁", "🍽️", "📝", "🏡", "🎉", "🧹"];
const TINTS = ["plum", "gold", "garnet"] as const;
type Tint = (typeof TINTS)[number];
const TINT_STYLES: Record<Tint, { bg: string; fg: string; dot: string }> = {
  plum: { bg: "bg-plum-100 dark:bg-night-surface-raised", fg: "text-plum-700 dark:text-night-accent", dot: "bg-plum-700" },
  gold: { bg: "bg-gold-100 dark:bg-night-surface-raised", fg: "text-gold-500", dot: "bg-gold-500" },
  garnet: { bg: "bg-garnet-100 dark:bg-night-surface-raised", fg: "text-garnet-600", dot: "bg-garnet-600" },
};

async function migrateLegacyShoppingList(coupleId: string, myUid: string, myName: string) {
  const marker = doc(db, "couples", coupleId, "lists", LEGACY_LIST_ID);
  const existing = await getDoc(marker);
  if (existing.exists()) return;
  const legacySnap = await getDocs(collection(db, "couples", coupleId, "shoppingItems"));
  if (legacySnap.empty) return;

  const batch = writeBatch(db);
  batch.set(marker, {
    name: "Einkaufsliste",
    icon: "🛒",
    createdByUid: myUid,
    createdByName: myName,
    createdAt: legacySnap.docs[0].data().createdAt ?? null,
  });
  for (const d of legacySnap.docs) {
    batch.set(doc(db, "couples", coupleId, "lists", LEGACY_LIST_ID, "items", d.id), d.data());
  }
  await batch.commit();
}

export function Lists() {
  const { user } = useAuth();
  const { couple, profile } = useCouple();
  const { items: lists, loading, add } = useCoupleCollection<WirList>(
    couple?.id ?? null,
    "lists",
    orderBy("createdAt", "asc")
  );

  useEffect(() => {
    markSeen("lists");
    return () => markSeen("lists");
  }, []);

  useEffect(() => {
    if (!couple || !user || !profile) return;
    migrateLegacyShoppingList(couple.id, user.uid, profile.displayName).catch(() => {
      // Best-effort: if this fails, the old shoppingItems/ data is still
      // intact and untouched, so nothing is lost — it just tries again
      // next time this page loads.
    });
  }, [couple, user, profile]);

  const [sheetOpen, setSheetOpen] = useState(false);
  const [name, setName] = useState("");
  const [icon, setIcon] = useState(ICON_CHOICES[0]);
  const [listType, setListType] = useState<"shopping" | "general">("general");
  const [submitting, setSubmitting] = useState(false);

  function openCreate() {
    setName("");
    setIcon(ICON_CHOICES[Math.floor(Math.random() * ICON_CHOICES.length)]);
    setListType("general");
    setSheetOpen(true);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting || !name.trim() || !user || !profile) return;
    setSubmitting(true);
    try {
      await add({ name: name.trim(), icon, type: listType, createdByUid: user.uid, createdByName: profile.displayName });
      setSheetOpen(false);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="mx-auto min-h-dvh max-w-md px-5 pb-28 pt-6">
      <h1 className="font-display text-[1.75rem] font-semibold tracking-tight text-plum-900 dark:text-night-accent">Listen</h1>
      <p className="mt-1 text-sm text-ink-400 dark:text-night-faint">Einkauf, Packen & mehr – alles an einem Ort.</p>

      {loading ? (
        <p className="mt-6 text-sm text-ink-400 dark:text-night-faint">Lädt…</p>
      ) : lists.length === 0 ? (
        <div className="mt-8 text-center">
          <p className="text-sm text-ink-400 dark:text-night-faint">Noch keine Liste angelegt.</p>
          <button
            onClick={openCreate}
            className="mt-4 h-11 rounded-xl bg-plum-700 px-5 text-sm font-semibold text-cream-50"
          >
            Erste Liste anlegen
          </button>
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-2 gap-x-3 gap-y-5">
          {lists.map((list, i) => (
            <ListCard key={list.id} list={list} index={i} coupleId={couple!.id} />
          ))}
          <button
            onClick={openCreate}
            className={`flex min-h-[9.5rem] flex-col items-center justify-center gap-1.5 rounded-2xl border-2 border-dashed border-cream-200 text-ink-400 transition-colors active:scale-[0.985] dark:border-night-border dark:text-night-faint ${
              lists.length % 2 === 1 ? "" : "mt-6"
            }`}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-6 w-6">
              <path d="M12 5v14M5 12h14" strokeLinecap="round" />
            </svg>
            <span className="text-xs font-medium">Neue Liste</span>
          </button>
        </div>
      )}

      <Fab onClick={openCreate} label="Neue Liste anlegen" />

      <Sheet open={sheetOpen} onClose={() => setSheetOpen(false)} title="Neue Liste">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <p className="mb-2 text-xs font-medium uppercase tracking-wider text-ink-400 dark:text-night-faint">Symbol</p>
            <div className="flex flex-wrap gap-2">
              {ICON_CHOICES.map((choice) => (
                <button
                  type="button"
                  key={choice}
                  onClick={() => setIcon(choice)}
                  className={`flex h-10 w-10 items-center justify-center rounded-full text-lg transition-colors ${
                    icon === choice ? "bg-plum-700" : "bg-cream-100 dark:bg-night-surface-raised"
                  }`}
                >
                  {choice}
                </button>
              ))}
            </div>
          </div>
          <input
            type="text"
            placeholder="z.B. Packliste Urlaub"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="h-12 w-full rounded-xl border border-cream-200 bg-white px-4 text-base text-ink-900 outline-none focus:border-plum-600 dark:border-night-border dark:bg-night-surface-raised dark:text-night-text"
          />
          <div>
            <p className="mb-2 text-xs font-medium uppercase tracking-wider text-ink-400 dark:text-night-faint">Art</p>
            <div className="flex gap-1.5">
              <button
                type="button"
                onClick={() => setListType("general")}
                className={`flex-1 rounded-xl border px-3 py-2.5 text-xs font-medium ${
                  listType === "general" ? "border-plum-700 bg-plum-700 text-cream-50" : "border-cream-200 text-ink-900 dark:border-night-border dark:text-night-text"
                }`}
              >
                Allgemeine Liste
              </button>
              <button
                type="button"
                onClick={() => setListType("shopping")}
                className={`flex-1 rounded-xl border px-3 py-2.5 text-xs font-medium ${
                  listType === "shopping" ? "border-plum-700 bg-plum-700 text-cream-50" : "border-cream-200 text-ink-900 dark:border-night-border dark:text-night-text"
                }`}
              >
                Einkaufsliste
              </button>
            </div>
            {listType === "shopping" && (
              <p className="mt-1.5 text-xs text-ink-400 dark:text-night-faint">Zeigt Lebensmittel-Kategorien beim Hinzufügen von Einträgen.</p>
            )}
          </div>
          <button
            type="submit"
            disabled={!name.trim() || submitting}
            className="h-12 w-full rounded-xl bg-plum-700 text-sm font-semibold text-cream-50 disabled:opacity-50"
          >
            {submitting ? "Wird angelegt…" : "Liste anlegen"}
          </button>
        </form>
      </Sheet>
    </main>
  );
}

function ListCard({ list, index, coupleId }: { list: WirList; index: number; coupleId: string }) {
  const { items } = useCoupleCollection<ListItem>(coupleId, `lists/${list.id}/items`, orderBy("createdAt", "asc"));
  const open = items.filter((i) => !i.done);
  const preview = (open.length > 0 ? open : items).slice(0, 4);
  const remaining = (open.length > 0 ? open : items).length - preview.length;
  const tint = TINTS[index % TINTS.length];
  const style = TINT_STYLES[tint];
  const stagger = index % 2 === 1;
  const tilt = index % 3 === 0 ? "" : index % 3 === 1 ? "-rotate-1" : "rotate-1";

  return (
    <Link
      to={`/lists/${list.id}`}
      className={`card card-interactive animate-fade-up flex min-h-[9.5rem] flex-col rounded-2xl p-4 ${tilt} ${
        stagger ? "mt-6" : ""
      }`}
      style={{ animationDelay: `${index * 40}ms` }}
    >
      <div className="flex items-center gap-2">
        <span className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-base ${style.bg}`}>
          {list.icon}
        </span>
        <p className="min-w-0 flex-1 truncate text-sm font-semibold text-ink-900 dark:text-night-text">{list.name}</p>
        {open.length > 0 && (
          <span className={`flex-shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${style.bg} ${style.fg}`}>
            {open.length}
          </span>
        )}
      </div>

      {items.length === 0 ? (
        <p className="mt-3 text-xs text-ink-400 dark:text-night-faint">Noch leer</p>
      ) : (
        <ul className="mt-3 space-y-1.5">
          {preview.map((item) => (
            <li key={item.id} className="flex items-center gap-1.5 text-xs">
              <span
                className={`h-2.5 w-2.5 flex-shrink-0 rounded-full border ${
                  item.done ? `${style.dot} border-transparent` : "border-cream-200 dark:border-night-border"
                }`}
              />
              <span className={`truncate ${item.done ? "text-ink-400 line-through dark:text-night-faint" : "text-ink-600 dark:text-night-muted"}`}>
                {item.text}
              </span>
            </li>
          ))}
          {remaining > 0 && <li className="pl-4 text-xs text-ink-400 dark:text-night-faint">+{remaining} weitere</li>}
        </ul>
      )}
    </Link>
  );
}
